import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import type {
  ActivityNotOpenToPublic,
  ActivityOpenToPublic,
} from '@/apiClient/v1'
import { useSignupJourneyContext } from '@/commons/context/SignupJourneyContext/SignupJourneyContext'
import { useEducationalDomains } from '@/commons/hooks/swr/useEducationalDomains'
import { ActivityNotOpenToPublicMap } from '@/commons/mappings/ActivityNotOpenToPublic'
import { ActivityOpenToPublicMap } from '@/commons/mappings/ActivityOpenToPublic'
import { toSelectOptions } from '@/commons/mappings/helpers'
import { pluralizeFr } from '@/commons/utils/pluralize'
import { FormLayout } from '@/components/FormLayout/FormLayout'
import { ScrollToFirstHookFormErrorAfterSubmit } from '@/components/ScrollToFirstErrorAfterSubmit/ScrollToFirstErrorAfterSubmit'
import { Banner } from '@/design-system/Banner/Banner'
import { Button } from '@/design-system/Button/Button'
import { ButtonColor, ButtonVariant } from '@/design-system/Button/types'
import { CheckboxGroup } from '@/design-system/CheckboxGroup/CheckboxGroup'
import { TextInput } from '@/design-system/TextInput/TextInput'
import fullMoreIcon from '@/icons/full-more.svg'
import fullTrashIcon from '@/icons/full-trash.svg'
import { MultiSelect, type Option } from '@/ui-kit/form/MultiSelect/MultiSelect'
import { PhoneNumberInput } from '@/ui-kit/form/PhoneNumberInput/PhoneNumberInput'
import { Select } from '@/ui-kit/form/Select/Select'

import styles from './ActivityForm.module.scss'

interface SocialUrl {
  url: string
}

export interface ActivityFormValues {
  activity?: ActivityOpenToPublic | ActivityNotOpenToPublic
  otherActivityComment?: string
  socialUrls: SocialUrl[]
  targetCustomer: {
    individual: boolean
    educational: boolean
  }
  phoneNumber: string
  culturalDomains?: string[]
}

export const ActivityForm = (): JSX.Element => {
  const { data: educationalDomains, isLoading: isLoadingEducationalDomains } =
    useEducationalDomains()
  const { offerer } = useSignupJourneyContext()

  const { register, control, formState, watch, setValue, trigger, setFocus } =
    useFormContext<ActivityFormValues>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'socialUrls',
  })

  const watchSocialUrls = watch('socialUrls')

  const mainActivityOptions =
    offerer?.isOpenToPublic === 'true'
      ? toSelectOptions(ActivityOpenToPublicMap)
      : toSelectOptions(ActivityNotOpenToPublicMap)

  const defaultCulturalDomain: Option[] | undefined = useMemo(() => {
    return educationalDomains.length === 0 ||
      !formState?.defaultValues?.culturalDomains
      ? undefined
      : educationalDomains
          .filter((apiDomain) =>
            formState.defaultValues?.culturalDomains?.find(
              (domain) => apiDomain.name === domain
            )
          )
          .map((domain) => {
            return { id: String(domain.id), label: domain.name }
          })
  }, [educationalDomains, formState.defaultValues])

  return (
    <>
      <ScrollToFirstHookFormErrorAfterSubmit />
      <FormLayout.Section>
        <FormLayout.Row mdSpaceAfter>
          <Select
            options={[
              {
                value: '',
                label: 'Sélectionnez votre activité principale',
              },
              ...mainActivityOptions,
            ]}
            {...register('activity')}
            error={formState.errors.activity?.message}
            label="Activité principale"
            required
          />
        </FormLayout.Row>

        {watch('activity') === 'OTHER' && (
          <FormLayout.Row mdSpaceAfter>
            <TextInput
              {...register(`otherActivityComment`)}
              label="Précisez votre activité"
              error={formState.errors.otherActivityComment?.message}
              maxCharactersCount={100}
              required
            />
          </FormLayout.Row>
        )}

        {!isLoadingEducationalDomains && (
          <FormLayout.Row mdSpaceAfter>
            <MultiSelect
              options={educationalDomains.map((educationalDomain) => ({
                id: String(educationalDomain.id),
                label: educationalDomain.name,
              }))}
              defaultOptions={defaultCulturalDomain}
              error={formState.errors.culturalDomains?.message}
              label="Domaine(s) d’activité"
              required={offerer?.isOpenToPublic === 'false'}
              onSelectedOptionsChanged={(selectedOptions, _y, _z) => {
                setValue(
                  'culturalDomains',
                  selectedOptions.length > 0
                    ? selectedOptions.map((opt) => opt.label)
                    : undefined
                )
              }}
              buttonLabel={
                (watch('culturalDomains') ?? []).length > 0
                  ? pluralizeFr(
                      (watch('culturalDomains') ?? []).length,
                      'domaine sélectionné',
                      'domaines sélectionnés'
                    )
                  : 'Sélectionnez un ou plusieurs domaines d’activité'
              }
            />
          </FormLayout.Row>
        )}

        <FormLayout.Row mdSpaceAfter>
          <PhoneNumberInput
            {...register('phoneNumber')}
            error={formState.errors.phoneNumber?.message}
            label={'Téléphone (utilisé uniquement par le pass Culture)'}
            required
          />
        </FormLayout.Row>

        <FormLayout.Row mdSpaceAfter className={styles['url-list']}>
          {fields.map((field, index) => (
            <FormLayout.Row key={field.id}>
              <TextInput
                {...register(`socialUrls.${index}.url`)}
                label="Site internet, réseau social"
                description="Format : https://www.siteinternet.com"
                type="url"
                required
                error={
                  formState.errors.socialUrls?.[index]?.url?.message ??
                  (index === 0
                    ? formState.errors.socialUrls?.root?.message
                    : undefined)
                }
                extension={
                  watchSocialUrls.length > 1 && (
                    <div
                      data-error={
                        formState.errors.socialUrls?.[index] ? 'true' : 'false'
                      }
                    >
                      <Button
                        variant={ButtonVariant.SECONDARY}
                        color={ButtonColor.NEUTRAL}
                        icon={fullTrashIcon}
                        onClick={() => {
                          remove(index)
                          setFocus(`socialUrls.${index - 1}.url`)
                        }}
                        disabled={watchSocialUrls.length <= 1}
                        tooltip={'Supprimer l’url'}
                      />
                    </div>
                  )
                }
              />
            </FormLayout.Row>
          ))}

          <Button
            variant={ButtonVariant.TERTIARY}
            icon={fullMoreIcon}
            onClick={() => {
              append({ url: '' })
            }}
            label="Ajouter un lien"
          />
        </FormLayout.Row>

        <FormLayout.Row className={styles['target-customer-row']}>
          <CheckboxGroup
            label="À qui souhaitez-vous destiner vos offres sur le pass Culture ? *"
            description="Sélectionnez au moins une option"
            options={[
              {
                label: 'Aux jeunes via l’application pass Culture',
                sizing: 'fill',
                checked: watch('targetCustomer.individual'),
                onChange: async (e) => {
                  setValue('targetCustomer.individual', e.target.checked)
                  await trigger('targetCustomer')
                },
              },
              {
                label: 'Aux groupes scolaires via ADAGE',
                sizing: 'fill',
                checked: watch('targetCustomer.educational'),
                onChange: async (e) => {
                  setValue('targetCustomer.educational', e.target.checked)
                  await trigger('targetCustomer')
                },
              },
            ]}
            variant="detailed"
            error={formState.errors.targetCustomer?.message}
          />
          <div className={styles['banner-container']}>
            <Banner title="Vous pourrez cumuler les deux types d'offres avec un seul compte pass Culture Pro." />
          </div>
        </FormLayout.Row>
      </FormLayout.Section>
    </>
  )
}
